import type { RemoteParticipant as OfficialTwilioRemoteParticipant } from "twilio-video";
import { logger } from "../../../../datadog/logger";
import { MediaKind } from "../../../core/room/participant/media/types";
import type { RemoteParticipant } from "../../../core/room/participant/RemoteParticipant";
import type {
  RemoteParticipantEvent,
  RemoteParticipantEventHandler,
  RemoteParticipantMediaEventMap,
} from "../../../core/room/participant/types";
import { TwilioRemoteParticipantEventHandler } from "./events";
import { detachRemoteTrack } from "./media/utils";
import {
  createTwilioEventHandler,
  getTwilioEvent,
  isEventSupported,
} from "./utils";
import { parseIdentity } from "../../../core/room/participant/utils";

const NO_OP_HANDLER = () => {
  logger.warn("[RemoteParticipant: on] Calling NO_OP detachEventListener");
};

/**
 * Vendor-agnostic wrapper for Twilio remote participants.
 * Delegates all event operations to the underlying Twilio participant
 * while providing a consistent interface for other video SDK vendors.
 *
 * This is intentionally not exported so the factory is the only way
 * to construct, ensuring uniqueness and consistent lifecycle management.
 */
export class TwilioRemoteParticipant implements RemoteParticipant {
  #participant: OfficialTwilioRemoteParticipant;

  constructor(twilioParticipant: OfficialTwilioRemoteParticipant) {
    this.#participant = twilioParticipant;
  }

  get isConnected() {
    return this.#participant.state === "connected";
  }

  get sid(): string {
    return this.#participant.sid;
  }

  get identity(): string {
    return this.#participant.identity;
  }

  get displayName(): string {
    const { identity } = this.#participant;
    return parseIdentity(identity)?.name ?? "Participant";
  }

  set participant(twilioParticipant: OfficialTwilioRemoteParticipant) {
    this.#participant = twilioParticipant;
  }

  on<K extends RemoteParticipantEvent>(
    event: K,
    handler: RemoteParticipantEventHandler<K>,
  ): () => void {
    if (!isEventSupported(event as string)) {
      logger.warn(
        `[TwilioRemoteParticipant: on] Unsupported event type: ${event}`,
      );
      return NO_OP_HANDLER;
    }

    const twilioEvent = getTwilioEvent(event);
    if (!twilioEvent) {
      logger.warn(
        `[TwilioRemoteParticipant: on] No Twilio mapping for event: ${event}`,
      );
      return NO_OP_HANDLER;
    }

    const twilioHandler = createTwilioEventHandler(event, handler);
    this.#participant.on(twilioEvent, twilioHandler);
    return () => this.#participant.off(twilioEvent, twilioHandler);
  }

  /**
   * Only present use case is for the "trackSubscribed" event.
   * Can be extended to support any other events.
   */
  async dispatchEvent<K extends keyof RemoteParticipantMediaEventMap>(
    event: K,
    handler: RemoteParticipantMediaEventMap[K],
    mediaKind: MediaKind,
  ): Promise<void> {
    if (!isEventSupported(event as string)) {
      logger.warn(
        `[TwilioRemoteParticipant: dispatchEvent] Unsupported event type: ${event}`,
      );
      return;
    }

    if (!getTwilioEvent(event)) {
      logger.warn(
        `[TwilioRemoteParticipant: dispatchEvent] No Twilio mapping for event: ${event}`,
      );
      return;
    }

    const remoteTracks = {
      audio: this.#participant.audioTracks,
      video: this.#participant.videoTracks,
      data: this.#participant.dataTracks,
    };

    const twilioHandler = createTwilioEventHandler(
      event,
      handler,
    ) as TwilioRemoteParticipantEventHandler["trackSubscribed"];

    for (const trackPublication of remoteTracks[mediaKind].values()) {
      if (!trackPublication.isSubscribed) continue;
      const track = trackPublication.track;
      if (!track) continue;

      await twilioHandler(track, trackPublication);

      if (mediaKind === "audio") {
        // For audio streams, we only process the first one (break after first iteration)
        // This matches the existing behavior in useRemoteAudio
        break;
      }
    }

    return;
  }

  detachAudio(audioElement: HTMLAudioElement): void {
    const audioTracks = this.#participant.audioTracks;
    for (const audioTrack of audioTracks.values()) {
      if (!audioTrack?.track) continue;
      detachRemoteTrack({ track: audioTrack.track, element: audioElement });
    }
  }
}
